﻿import {
  AlertTriangle,
  Archive,
  ArrowLeft,
  ExternalLink,
  Loader2,
  Pause,
  Play,
} from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import type { ShopStatus } from "@ticketnjoy/types";

import { DashboardShell } from "@/components/dashboard-shell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuthenticatedMe } from "@/features/organizers/api";
import {
  useShopQuery,
  useShopStatusMutation,
  useUpdateShopMutation,
} from "@/features/shops/api";
import { ShopForm, type EditShopFormValues } from "@/features/shops/shop-form";
import { ShopEventsSection } from "@/features/shop-events/shop-events-section";
import { ApiError } from "@/lib/api-client";

const STATUS_LABELS: Record<ShopStatus, string> = {
  Active: "Aktif",
  Paused: "Duraklatıldı",
  Archived: "Arşivde",
};

const STATUS_VARIANTS: Record<ShopStatus, "success" | "warning" | "secondary"> = {
  Active: "success",
  Paused: "warning",
  Archived: "secondary",
};

export function ShopDetailPage() {
  const params = useParams<{ slug: string }>();
  const slug = params.slug ?? "";
  const navigate = useNavigate();

  const me = useAuthenticatedMe();
  const shopQuery = useShopQuery(slug);
  const update = useUpdateShopMutation(slug);
  const statusMutation = useShopStatusMutation(slug);

  if (!me.data) return null;

  if (shopQuery.isLoading) {
    return (
      <DashboardShell companyName={me.data.companyName}>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardShell>
    );
  }

  if (shopQuery.error || !shopQuery.data) {
    return (
      <DashboardShell companyName={me.data.companyName}>
        <Card className="max-w-md mx-auto text-center">
          <CardHeader>
            <CardTitle>Mağaza bulunamadı</CardTitle>
            <CardDescription>
              Bu mağaza silinmiş olabilir veya erişim yetkin yok.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={() => navigate("/")}>
              Dashboard&apos;a Dön
            </Button>
          </CardContent>
        </Card>
      </DashboardShell>
    );
  }

  const shop = shopQuery.data;
  const isArchived = shop.status === "Archived";
  const shopBaseUrl =
    import.meta.env.VITE_SHOP_BASE_URL ?? "http://localhost:3002";
  const publicShopUrl = `${shopBaseUrl}/${shop.slug}`;
  // Mağazayı yayına alabilmek için organizer'ın yasal bilgileri tam olmalı
  // (backend ChangeStatusAsync guard'ı bunu enforce ediyor — UI'da da engelle
  // ki kullanıcı submit etmeden sebebini görsün).
  const canActivate = me.data.isLegalInfoComplete;

  async function handleUpdate(values: EditShopFormValues) {
    try {
      await update.mutateAsync({
        name: values.name,
        description: emptyToNull(values.description),
        logoUrl: emptyToNull(values.logoUrl),
        coverImageUrl: emptyToNull(values.coverImageUrl),
        // themeColor UI'dan kaldırıldı; mevcut değeri round-trip ediyoruz
        // (aksi halde backend null'layacak). v2 tema ayarları bölümünde geri gelecek.
        themeColor: shop.themeColor,
        contactEmail: emptyToNull(values.contactEmail),
        contactPhone: emptyToNull(values.contactPhone),
        defaultLocale: values.defaultLocale,
        heroStyle: values.heroStyle,
      });
      toast.success("Mağaza güncellendi.");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.detail : "Güncelleme başarısız.");
    }
  }

  async function handleStatusChange(action: "pause" | "activate" | "archive") {
    const confirmMsg =
      action === "archive"
        ? "Mağazayı arşivlemek istediğine emin misin? Bu işlem geri alınamaz."
        : null;
    if (confirmMsg && !window.confirm(confirmMsg)) return;

    try {
      await statusMutation.mutateAsync(action);
      toast.success(
        action === "pause"
          ? "Mağaza duraklatıldı."
          : action === "activate"
            ? "Mağaza aktif edildi."
            : "Mağaza arşivlendi.",
      );
    } catch (err) {
      toast.error(err instanceof ApiError ? err.detail : "İşlem başarısız.");
    }
  }

  return (
    <DashboardShell companyName={me.data.companyName}>
      <div className="mb-6">
        <Button variant="ghost" size="sm" asChild className="mb-3 -ml-2">
          <Link to="/">
            <ArrowLeft className="mr-1 h-4 w-4" />
            Dashboard
          </Link>
        </Button>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold">{shop.name}</h1>
              <Badge variant={STATUS_VARIANTS[shop.status]}>
                {STATUS_LABELS[shop.status]}
              </Badge>
            </div>
            <a
              href={publicShopUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 inline-block font-mono text-sm text-muted-foreground transition hover:text-foreground"
              title="Mağazanı yeni sekmede aç"
            >
              {publicShopUrl}
            </a>
          </div>
          <Button
            variant="outline"
            size="sm"
            asChild
            disabled={isArchived}
            title={isArchived ? "Arşivlenmiş mağaza görüntülenemez" : "Yeni sekmede aç"}
          >
            <a
              href={publicShopUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-disabled={isArchived}
              onClick={(e) => {
                if (isArchived) e.preventDefault();
              }}
            >
              <ExternalLink className="mr-1.5 h-4 w-4" />
              Mağazayı Görüntüle
            </a>
          </Button>
        </div>
      </div>

      {!isArchived && (
        <Card className="mb-6">
          <CardContent className="pt-6">
            <ShopEventsSection shopSlug={slug} />
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Genel Ayarlar</CardTitle>
            <CardDescription>Mağazanın görünen adı ve içeriği.</CardDescription>
          </CardHeader>
          <CardContent>
            <fieldset disabled={isArchived} className="space-y-4">
              <ShopForm
                mode="edit"
                shopId={shop.id}
                defaultValues={{
                  name: shop.name,
                  description: shop.description ?? "",
                  defaultLocale: shop.defaultLocale,
                  logoUrl: shop.logoUrl ?? "",
                  coverImageUrl: shop.coverImageUrl ?? "",
                  contactEmail: shop.contactEmail ?? "",
                  contactPhone: shop.contactPhone ?? "",
                  heroStyle: shop.heroStyle,
                }}
                onSubmit={handleUpdate}
                isSubmitting={update.isPending}
              />
              {isArchived && (
                <p className="text-sm text-muted-foreground">
                  Arşivlenmiş mağazalar düzenlenemez.
                </p>
              )}
            </fieldset>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Durum</CardTitle>
              <CardDescription>
                Duraklatılmış mağazada yeni satış yapılamaz, ancak verisi korunur.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {shop.status === "Active" && (
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => handleStatusChange("pause")}
                  disabled={statusMutation.isPending}
                >
                  <Pause className="mr-2 h-4 w-4" />
                  Duraklat
                </Button>
              )}
              {shop.status === "Paused" && (
                <>
                  {!canActivate && (
                    <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/50 dark:text-amber-200">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <div className="space-y-1">
                        <p>
                          Mağazayı yayına alabilmek için önce{" "}
                          <Link
                            to="/settings"
                            className="font-medium underline hover:no-underline"
                          >
                            Yasal Bilgiler
                          </Link>
                          &apos;i doldur (firma unvanı, vergi no, adres).
                        </p>
                      </div>
                    </div>
                  )}
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => handleStatusChange("activate")}
                    disabled={statusMutation.isPending || !canActivate}
                    title={
                      !canActivate
                        ? "Yasal bilgiler eksik"
                        : "Mağazayı yayına al"
                    }
                  >
                    <Play className="mr-2 h-4 w-4" />
                    Aktif Et
                  </Button>
                </>
              )}
              {!isArchived && (
                <Button
                  variant="outline"
                  className="w-full justify-start text-destructive hover:text-destructive"
                  onClick={() => handleStatusChange("archive")}
                  disabled={statusMutation.isPending}
                >
                  <Archive className="mr-2 h-4 w-4" />
                  Arşivle
                </Button>
              )}
              {isArchived && (
                <p className="text-sm text-muted-foreground">
                  Bu mağaza arşivlenmiş durumda.
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Bilgi</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5 text-sm text-muted-foreground">
              <p>
                <span className="text-foreground">Varsayılan Dil:</span>{" "}
                <span className="uppercase">{shop.defaultLocale}</span>
              </p>
              <p>
                <span className="text-foreground">Oluşturuldu:</span>{" "}
                {new Date(shop.createdAt).toLocaleDateString("tr-TR")}
              </p>
              <p>
                <span className="text-foreground">Güncellendi:</span>{" "}
                {new Date(shop.updatedAt).toLocaleDateString("tr-TR")}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardShell>
  );
}

function emptyToNull(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}
